import React, { useState, useMemo } from 'react';

const CATEGORIES = [
  { id: 'up_sensitive', label: 'Up + Sensitive', color: '#27ae60' },
  { id: 'down_sensitive', label: 'Down + Sensitive', color: '#2471a3' },
  { id: 'up_insensitive', label: 'Up + Insensitive', color: '#e67e22' },
  { id: 'down_insensitive', label: 'Down + Insensitive', color: '#c0392b' },
];

export default function EnrichmentTable({ enrichment, onSelectGene }) {
  const [activeCategory, setActiveCategory] = useState('up_sensitive');
  const [sortBy, setSortBy] = useState('score');
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');

  const terms = useMemo(() => enrichment?.[activeCategory] || [], [enrichment, activeCategory]);

  const sources = useMemo(() => {
    const s = new Set(terms.map(t => t.source));
    return ['all', ...Array.from(s).sort()];
  }, [terms]);

  const sortedTerms = useMemo(() => {
    let filtered = sourceFilter === 'all' ? terms : terms.filter(t => t.source === sourceFilter);
    return [...filtered].sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === 'adjPval') {
        // Lower p-value is "better", so ascending = best first
        return sortAsc ? vb - va : va - vb;
      }
      return sortAsc ? va - vb : vb - va;
    });
  }, [terms, sortBy, sortAsc, sourceFilter]);

  const maxScore = useMemo(() => {
    if (sortedTerms.length === 0) return 1;
    return Math.max(...sortedTerms.map(t => t.score || 0));
  }, [sortedTerms]);

  const handleSort = (key) => {
    if (sortBy === key) setSortAsc(!sortAsc);
    else { setSortBy(key); setSortAsc(key === 'adjPval'); }
  };

  const handleCategoryChange = (id) => {
    setActiveCategory(id);
    setExpandedRow(null);
    setSourceFilter('all');
  };

  const catInfo = CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="enrichment-container">
      <div className="enrichment-tabs">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={activeCategory === cat.id ? 'active' : ''}
            style={{
              borderBottomColor: activeCategory === cat.id ? cat.color : 'transparent',
              color: activeCategory === cat.id ? cat.color : '#aaa',
            }}
            onClick={() => handleCategoryChange(cat.id)}
          >
            {cat.label}
            <span className="enrichment-count">{(enrichment?.[cat.id] || []).length}</span>
          </button>
        ))}
      </div>

      <div className="enrichment-controls">
        <div className="enrichment-filter">
          <label>Source:</label>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
            {sources.map(s => (
              <option key={s} value={s}>{s === 'all' ? 'All sources' : s.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
        <span className="enrichment-summary">
          {sortedTerms.length} terms | {catInfo?.label}
        </span>
      </div>

      <div className="enrichment-table-wrapper">
        <table className="enrichment-table">
          <thead>
            <tr>
              <th className="enrichment-th-term" onClick={() => handleSort('term')}>
                Term {sortBy === 'term' ? (sortAsc ? '\u25b2' : '\u25bc') : ''}
              </th>
              <th onClick={() => handleSort('source')}>Source</th>
              <th>Overlap</th>
              <th className="enrichment-th-num" onClick={() => handleSort('adjPval')}>
                Adj. P {sortBy === 'adjPval' ? (sortAsc ? '\u25b2' : '\u25bc') : ''}
              </th>
              <th className="enrichment-th-num" onClick={() => handleSort('or')}>
                OR {sortBy === 'or' ? (sortAsc ? '\u25b2' : '\u25bc') : ''}
              </th>
              <th className="enrichment-th-score" onClick={() => handleSort('score')}>
                Score {sortBy === 'score' ? (sortAsc ? '\u25b2' : '\u25bc') : ''}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTerms.map((term, i) => (
              <React.Fragment key={`${term.source}-${term.term}-${i}`}>
                <tr
                  className={`enrichment-row ${expandedRow === i ? 'expanded' : ''}`}
                  onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                >
                  <td className="enrichment-term">
                    <span className="enrichment-expand">{expandedRow === i ? '\u25bc' : '\u25b6'}</span>
                    {term.term}
                  </td>
                  <td className="enrichment-source">{term.source?.replace(/_/g, ' ')}</td>
                  <td>{term.overlap}</td>
                  <td className="enrichment-num">{term.adjPval?.toExponential(1)}</td>
                  <td className="enrichment-num">{term.or?.toFixed(1)}</td>
                  <td className="enrichment-score-cell">
                    <div className="enrichment-score-bar-bg">
                      <div
                        className="enrichment-score-bar"
                        style={{
                          width: `${Math.min(100, (term.score / maxScore) * 100)}%`,
                          backgroundColor: catInfo?.color || '#4ecdc4',
                        }}
                      />
                    </div>
                    <span>{term.score?.toFixed(0)}</span>
                  </td>
                </tr>
                {expandedRow === i && (
                  <tr className="enrichment-genes-row">
                    <td colSpan={6}>
                      <div className="enrichment-genes">
                        {term.genes?.map(g => (
                          <button
                            key={g}
                            className="enrichment-gene-btn"
                            onClick={(e) => { e.stopPropagation(); if (onSelectGene) onSelectGene(g); }}
                          >
                            {g}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
